/**
 * ARIA Service Orders Modal
 * Version: 1.0.0
 *
 * Modal for browsing, selecting, and scheduling service orders.
 */

import { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { X, Search, Calendar, User, Clock, CheckCircle } from "lucide-react";
import { clsx } from "clsx";
import type { Job, JobStatus, JobType } from "../types";
import { IdentifierBadge } from "../components/jobs/IdentifierBadge";
import { SortableHeader } from "./table/SortableHeader";
import type { SortDirection } from "../utils/sort";

const JOBS_QUERY = gql`
  query Jobs($page: Int, $pageSize: Int, $status: JobStatus, $search: String, $sortField: String, $sortDirection: SortDirection) {
    jobs(page: $page, pageSize: $pageSize, status: $status, search: $search, sortField: $sortField, sortDirection: $sortDirection) {
      items {
        id
        sytelineOrderId
        sytelineOrderNumber
        seNumber
        jobType
        status
        priority
        identifierType
        customerName
        customerPhone
        fullAddress
        city
        province
        scheduling {
          scheduledDate
          startTime
          appointmentDescription
        }
        technician {
          id
          fullName
        }
        parts {
          status
          eta
        }
        description
        jobSummary
        createdAt
      }
      total
      page
      pageSize
    }
  }
`;

const UPDATE_JOB_MUTATION = gql`
  mutation UpdateJob($id: ID!, $input: UpdateJobInput!) {
    updateJob(id: $id, input: $input) {
      id
      status
      scheduling {
        scheduledDate
        startTime
        endTime
      }
      technicians {
        id
        fullName
      }
    }
  }
`;

const AVAILABLE_TECHNICIANS_QUERY = gql`
  query AvailableTechnicians {
    technicians {
      items {
        id
        fullName
        employmentStatus
        isAvailable
        zone {
          id
          name
        }
      }
    }
  }
`;

interface TechnicianOption {
  id: string;
  fullName: string;
  employmentStatus: string;
  isAvailable: boolean;
  zone?: { id: string; name: string };
}

const typeLabels: Record<JobType, string> = {
  installation: "Installation",
  service: "Service",
  warranty: "Warranty",
  inspection: "Inspection",
  callback: "Callback",
  estimate: "Estimate",
};

interface ServiceOrdersModalProps {
  isOpen: boolean;
  onClose: () => void;
  onJobCreated: () => void;
  selectedDate?: Date;
}

export function ServiceOrdersModal({
  isOpen,
  onClose,
  onJobCreated,
  selectedDate,
}: ServiceOrdersModalProps) {
  const [step, setStep] = useState<"list" | "schedule">("list");
  const [search, setSearch] = useState("");
  const [filterStatus] = useState<JobStatus | "">();
  const [page, setPage] = useState(1);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirectionState] = useState<SortDirection>("asc");

  const handleSort = (field: string) => {
    setSortDirectionState((prev) => (sortField === field && prev === "asc" ? "desc" : "asc"));
    setSortField(field);
    setPage(1);
  };

  const [scheduledDate, setScheduledDate] = useState(
    selectedDate
      ? selectedDate.toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0],
  );
  const [startTime, setStartTime] = useState("09:00");
  const [technicianIds, setTechnicianIds] = useState<string[]>([]);
  const [appointmentDescription, setAppointmentDescription] = useState("");

  const { data, loading } = useQuery(JOBS_QUERY, {
    variables: {
      page,
      pageSize: 20,
      status: filterStatus || undefined,
      search: search || undefined,
      sortField: sortField || undefined,
      sortDirection: sortField ? sortDirection.toUpperCase() : undefined,
    },
    skip: !isOpen,
  });

  const { data: techData } = useQuery(AVAILABLE_TECHNICIANS_QUERY, {
    skip: !isOpen || step !== "schedule",
  });

  const [updateJob, { loading: scheduling }] = useMutation(UPDATE_JOB_MUTATION);

  const technicians = (techData?.technicians?.items || []).filter(
    (t: TechnicianOption) => t.employmentStatus === "ACTIVE" && t.isAvailable,
  ) as TechnicianOption[];

  const jobs = (data?.jobs?.items || []) as Job[];
  const total = data?.jobs?.total || 0;

  const handleClose = () => {
    setStep("list");
    setSelectedJob(null);
    setTechnicianIds([]);
    setAppointmentDescription("");
    onClose();
  };

  const handleScheduleJob = async () => {
    if (!selectedJob || technicianIds.length === 0) return;
    try {
      const scheduledDateTime = new Date(`${scheduledDate}T${startTime}:00`);
      await updateJob({
        variables: {
          id: selectedJob.id,
          input: {
            status: "SCHEDULED",
            technicianIds,
            scheduling: {
              scheduledDate: scheduledDateTime.toISOString(),
              startTime: scheduledDateTime.toISOString(),
              appointmentDescription: appointmentDescription || undefined,
            },
          },
        },
      });
      onJobCreated();
      handleClose();
    } catch (error) {
      console.error("Failed to schedule job:", error);
      alert("Failed to schedule job. Please try again.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {step === "list"
                  ? "Select Service Order"
                  : "Schedule Service Order"}
              </h2>
              {step === "schedule" && selectedJob && (
                <p className="text-sm text-gray-500">
                  {selectedJob.customerName} — {selectedJob.fullAddress}
                </p>
              )}
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {step === "list" ? (
              <>
                <div className="flex gap-4 mb-6">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search by order ID, customer name, or address..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="input pl-10"
                    />
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-12 text-gray-500">
                    Loading Service Orders...
                  </div>
                ) : jobs.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    No service orders found
                  </div>
                ) : (
                  <div className="card overflow-x-auto overflow-y-hidden">
                    <table className="w-full table-fixed">
                      <thead className="bg-gray-50 border-b border-gray-200">
                        <tr>
                          <SortableHeader label="SE Number" field="seNumber" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                          {/* Sorts by customerName — the primary content of this composite column */}
                          <SortableHeader label="Customer & Technician" field="customerName" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                          <SortableHeader label="Full Address" field="fullAddress" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                          <SortableHeader label="Appointment" field="appointment" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                          <SortableHeader label="Service Order Summary" field="jobSummary" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                          <SortableHeader label="Type" field="jobType" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                          <SortableHeader label="Technician" field="technician" activeField={sortField} activeDirection={sortDirection} onSort={handleSort} />
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {jobs.map((job) => (
                          <JobRow
                            key={job.id}
                            job={job}
                            selected={selectedJob?.id === job.id}
                            onClick={() => setSelectedJob(job)}
                          />
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
                      <p className="text-sm text-gray-500">
                        Showing {(page - 1) * 20 + 1} to{" "}
                        {Math.min(page * 20, total)} of {total} results
                      </p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setPage((p) => Math.max(1, p - 1))}
                          disabled={page === 1}
                          className="btn-secondary text-sm disabled:opacity-50"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => setPage((p) => p + 1)}
                          disabled={page * 20 >= total}
                          className="btn-secondary text-sm disabled:opacity-50"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="space-y-6">
                {/* Schedule Appointment */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Schedule Appointment
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Date *
                      </label>
                      <input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        className="input w-full"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time *
                      </label>
                      <input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="input w-full"
                        required
                      />
                    </div>
                  </div>
                </section>

                {/* Assign Technicians */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Assign Technicians
                    {technicianIds.length > 0 && (
                      <span className="ml-auto text-xs font-normal text-brand-600 normal-case">
                        {technicianIds.length} selected
                      </span>
                    )}
                  </h3>
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-52 overflow-y-auto">
                    {technicians.length === 0 ? (
                      <p className="px-3 py-4 text-sm text-gray-400 text-center">No available technicians</p>
                    ) : (
                      technicians.map((tech) => {
                        const isSelected = technicianIds.includes(tech.id);
                        const orderIdx = technicianIds.indexOf(tech.id);
                        return (
                          <button
                            key={tech.id}
                            type="button"
                            onClick={() =>
                              isSelected
                                ? setTechnicianIds(technicianIds.filter((id) => id !== tech.id))
                                : setTechnicianIds([...technicianIds, tech.id])
                            }
                            className={clsx(
                              "w-full flex items-center justify-between px-3 py-2.5 text-left transition-colors",
                              isSelected ? "bg-brand-50" : "hover:bg-gray-50"
                            )}
                          >
                            <div>
                              <span className="text-sm font-medium text-gray-900">{tech.fullName}</span>
                              {tech.zone && (
                                <span className="text-xs text-gray-400 ml-2">{tech.zone.name}</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0 ml-3">
                              {isSelected && orderIdx === 0 && (
                                <span className="text-xs bg-brand-600 text-white px-1.5 py-0.5 rounded-full">Lead</span>
                              )}
                              {isSelected ? (
                                <CheckCircle className="w-4 h-4 text-brand-600" />
                              ) : (
                                <div className="w-4 h-4 rounded-full border-2 border-gray-300" />
                              )}
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                  {technicianIds.length === 0 && (
                    <p className="text-xs text-gray-400 mt-1.5">Select at least one. First selected becomes the lead.</p>
                  )}
                </section>

                {/* Appointment Notes */}
                <section>
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Appointment Notes
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Appointment Description
                    </label>
                    <input
                      type="text"
                      value={appointmentDescription}
                      onChange={(e) =>
                        setAppointmentDescription(e.target.value)
                      }
                      className="input w-full"
                      placeholder="e.g., Morning appointment - customer working from home"
                    />
                  </div>
                </section>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-between">
            {step === "list" ? (
              <>
                <button onClick={handleClose} className="btn-secondary">
                  Cancel
                </button>
                <button
                  onClick={() => setStep("schedule")}
                  disabled={!selectedJob}
                  className="btn-primary disabled:opacity-50"
                >
                  Schedule Service Order
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setStep("list")}
                  className="btn-secondary"
                >
                  Back
                </button>
                <button
                  onClick={handleScheduleJob}
                  disabled={
                    scheduling || technicianIds.length === 0 || !scheduledDate || !startTime
                  }
                  className="btn-primary disabled:opacity-50"
                >
                  {scheduling ? "Scheduling..." : "Confirm Schedule"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface JobRowProps {
  job: Job;
  selected: boolean;
  onClick: () => void;
}

function JobRow({ job, selected, onClick }: JobRowProps) {
  return (
    <tr
      className={clsx(
        "cursor-pointer",
        selected
          ? "bg-brand-50 hover:bg-brand-100"
          : "hover:bg-gray-50",
      )}
      onClick={onClick}
    >
      {/* SE Number */}
      <td className="px-4 py-3 align-top">
        <div className="font-medium text-gray-900">
          {job.seNumber || job.sytelineOrderNumber}
        </div>
        <div className="text-xs text-gray-500">{job.sytelineOrderId}</div>
      </td>

      {/* Customer & Technician */}
      <td className="px-4 py-3 align-top">
        <div className="text-gray-900 text-sm">{job.customerName || "-"}</div>
        {job.customerPhone && (
          <div className="text-xs text-gray-500">{job.customerPhone}</div>
        )}
        <div className="mt-1 text-xs text-gray-500">
          <span className="font-medium">Tech:</span>{" "}
          {job.technician?.fullName || "Unassigned"}
        </div>
      </td>

      {/* Full Address */}
      <td className="px-4 py-3 align-top">
        <div
          className="text-gray-900 text-sm whitespace-normal break-words"
          title={job.fullAddress}
        >
          {job.fullAddress}
        </div>
      </td>

      {/* Appointment */}
      <td className="px-4 py-3 align-top">
        <div className="text-gray-900 text-sm">
          {job.scheduling?.appointmentDescription || job.description || "-"}
        </div>
        {job.scheduling?.scheduledDate && (
          <div className="text-xs text-gray-500 mt-1">
            {new Date(job.scheduling.scheduledDate).toLocaleDateString()}
            {job.scheduling.startTime &&
              ` at ${new Date(job.scheduling.startTime).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}`}
          </div>
        )}
      </td>

      {/* Service Order Summary */}
      <td className="px-4 py-3 align-top">
        <div
          className="text-gray-900 text-sm whitespace-normal break-words"
          title={job.jobSummary || job.description}
        >
          {job.jobSummary || job.description || "-"}
        </div>
      </td>

      {/* Type */}
      <td className="px-4 py-3 align-top">
        <span
          className={clsx(
            "badge",
            job.jobType === "installation" && "badge-blue",
            job.jobType === "service" && "badge-green",
            job.jobType === "warranty" && "badge-purple",
            job.jobType === "estimate" && "badge-yellow",
            job.jobType === "callback" && "badge-red",
            job.jobType === "inspection" && "badge-gray",
          )}
        >
          {typeLabels[job.jobType] || job.jobType}
        </span>
        <IdentifierBadge type={job.identifierType} />
      </td>

      {/* Technician */}
      <td className="px-4 py-3">
        <div className="text-gray-900">
          {job.technician?.fullName || "Unassigned"}
        </div>
      </td>
    </tr>
  );
}
